import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, ArrowRight, Hand, Leaf, Sun, Heart } from "lucide-react";
import { Reveal, PageShell, EASE } from "@/components/Reveal";

const CHAPTERS = [
  {
    to: "/shiatsu",
    title: "Shiatsu",
    sub: "Berührung, die in die Tiefe geht",
    img: "/images/karte-shiatsu.png",
    alt: "Carmen behandelt eine Klientin mit achtsamer Shiatsu-Berührung am Kopf",
    icon: Hand,
    id: "shiatsu",
  },
  {
    to: "/yoga",
    title: "Yoga",
    sub: "Kraft, Beweglichkeit und innere Ruhe",
    img: "/images/karte-yoga.webp",
    alt: "Yoga-Gruppe sitzt mit erhobenen Armen auf einem Holzsteg in der Natur",
    icon: Leaf,
    id: "yoga",
  },
  {
    to: "/aktuelles",
    title: "Aktuelles",
    sub: "Räume für Begegnung, Bewegung und neue Erfahrungen",
    img: "/images/karte-aktuelles.webp",
    alt: "Gruppe liegt im Kreis auf einer Wiese unter Bäumen",
    icon: Sun,
    id: "aktuelles",
  },
  {
    to: "/begleitung",
    title: "1:1 Begleitung",
    sub: "Zurück in deine Kraft – verbunden mit dir.",
    img: "/images/begleitung-strand-herz.webp",
    alt: "In den Sand gezeichnetes Herz am Strand unter strahlender Sonne",
    icon: Heart,
    id: "begleitung",
  },
];

export default function Home() {
  return (
    <PageShell testid="home-page">
      <section data-testid="hero-section" className="relative overflow-hidden">
        <motion.img
          src="/images/strand-original.jpg"
          alt="Carmen sitzt bei Sonnenuntergang im Schneidersitz am Strand und blickt aufs Meer"
          className="block aspect-[4/5] w-full object-cover object-[50%_100%] [@media(orientation:landscape)_and_(max-width:1023px)]:aspect-[16/9] lg:aspect-auto lg:h-auto lg:object-[50%_35%]"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1.6, ease: EASE }}
        />
        <div className="absolute inset-x-0 top-[max(7%,6.25rem)] z-10 px-6 [@media(orientation:landscape)_and_(max-width:1023px)]:top-[max(5%,5rem)] lg:top-0 lg:px-12 lg:pt-32">
          <div className="mx-auto w-full max-w-7xl text-center">
            <h1>
              <span
                className="block animate-hero-fade font-serif text-[clamp(1.35rem,6.5vw,2.25rem)] uppercase leading-[1.2] tracking-[0.1em] text-weinrot [text-shadow:0_1px_24px_rgba(245,240,233,0.85)] [@media(orientation:landscape)_and_(max-width:1023px)]:text-[clamp(1.3rem,4.8vw,2rem)] lg:text-[clamp(3rem,4.4vw,3.75rem)]"
                style={{ animationDelay: "0.2s" }}
              >
                Dein Körper spricht mit dir
              </span>
              <span
                className="mx-auto mt-4 block max-w-2xl animate-hero-fade text-center font-serif text-[clamp(1.25rem,4.6vw,1.6rem)] font-medium leading-relaxed text-weinrot [text-shadow:0_1px_18px_rgba(245,240,233,0.9)] lg:mt-6 lg:text-4xl"
                style={{ animationDelay: "0.45s" }}
              >
                Bist du bereit, ihm zu lauschen?
              </span>
            </h1>
          </div>
        </div>
      </section>

      <section data-testid="raum-section" className="px-6 pt-16 md:px-12 md:pt-24">
        <div className="mx-auto max-w-3xl">
          <Reveal>
            <div className="rounded-[2.5rem] bg-karte px-8 py-14 text-center md:px-14 md:py-20">
              <h2
                data-testid="raum-titel"
                className="font-serif text-4xl leading-tight text-rot md:text-6xl"
              >
                Ein Raum für dich.
              </h2>
              <p className="mt-7 font-serif text-2xl leading-snug text-rot md:mt-9 md:text-3xl">
                Für deinen Körper. Für deine Seele.
                <br />
                Für dein Leben.
              </p>
              <p className="mx-auto mt-9 max-w-xl text-base leading-loose text-anthrazit/85 md:mt-11 md:text-lg">
                Hier findest du einen Ort, an dem du zur Ruhe kommen, neue Kraft schöpfen und wieder
                in Verbindung mit dir selbst treten kannst.
              </p>
              <p className="mx-auto mt-5 max-w-xl text-base leading-loose text-anthrazit/85 md:text-lg">
                Mit achtsamer Berührung, bewusstem Atem und Bewegung begleite ich dich auf deinem
                Weg zurück zu dir.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <section data-testid="intro-section" className="px-6 py-24 md:px-12 md:py-40">
        <div className="mx-auto max-w-3xl">
          <Reveal>
            <p
              data-testid="intro-statement"
              className="font-serif text-3xl leading-snug text-rot md:text-[2.6rem] md:leading-[1.25]"
            >
              Dein Körper spricht mit dir ist eine Einladung, wieder mehr Lebendigkeit, Ruhe und Verbindung in dein
              Leben zu bringen.
            </p>
          </Reveal>
          <Reveal delay={0.1} className="mt-14 md:mt-20">
            <p className="text-base leading-loose text-anthrazit/85 md:text-lg">
              In meiner Arbeit geht es darum, diesen Raum wieder zu öffnen. Einen Raum, in dem dein
              Nervensystem zur Ruhe kommen kann, du deinen Körper wieder bewusster wahrnimmst und
              die Verbindung zu dir selbst stärkst. Nicht, um noch mehr an dir zu arbeiten, sondern
              um wieder Zugang zu dem zu bekommen, was längst in dir angelegt ist.
            </p>
          </Reveal>
        </div>
      </section>

      <section data-testid="kapitel-section" className="px-6 py-24 md:px-12 md:py-32">
        <div className="mx-auto max-w-7xl">
          <Reveal>
            <h2
              data-testid="kapitel-titel"
              className="mb-14 font-serif text-3xl font-normal leading-snug text-rot md:mb-16 md:text-4xl"
            >
              So kann ich dich begleiten:
            </h2>
          </Reveal>
          <div className="grid items-start gap-8 sm:grid-cols-2 xl:grid-cols-4">
            {CHAPTERS.map((c, i) => (
              <Reveal key={c.id} delay={i * 0.1} className="h-full">
                <Link
                  to={c.to}
                  data-testid={`kapitel-link-${c.id}`}
                  className="group flex h-full flex-col overflow-hidden rounded-2xl border border-anthrazit/10 bg-karte shadow-[0_20px_45px_-28px_rgba(53,51,48,0.3)] transition-all duration-500 hover:-translate-y-1.5 hover:shadow-[0_32px_55px_-28px_rgba(53,51,48,0.4)]"
                >
                  <div className="relative">
                    {c.img ? (
                      <img
                        src={c.img}
                        alt={c.alt}
                        loading="lazy"
                        className="block h-auto w-full"
                      />
                    ) : (
                      <div
                        data-testid="kapitel-platzhalter-ueber-mich"
                        className="flex aspect-[4/3] w-full items-center justify-center border-b border-dashed border-rot/30 bg-creme"
                      >
                        <span className="text-xs uppercase tracking-[0.3em] text-anthrazit/45">
                          Bild folgt in Kürze
                        </span>
                      </div>
                    )}
                    <span className="absolute -bottom-7 left-1/2 flex h-14 w-14 -translate-x-1/2 items-center justify-center rounded-full bg-karte text-rot shadow-[0_12px_24px_-10px_rgba(53,51,48,0.35)]">
                      <c.icon size={22} strokeWidth={1.5} />
                    </span>
                  </div>
                  <div className="flex flex-1 flex-col items-center px-7 pb-9 pt-12 text-center">
                    <h3 className="font-serif text-xl uppercase tracking-[0.18em] text-rot md:text-2xl">
                      {c.title}
                    </h3>
                    <p className="mt-4 text-sm leading-relaxed text-anthrazit/80 md:text-base">
                      {c.sub}
                    </p>
                    <span className="mt-auto inline-flex items-center gap-2 pt-8 text-sm tracking-[0.12em] text-rot underline decoration-rot/50 underline-offset-8 transition-colors duration-300 group-hover:decoration-rot">
                      Mehr erfahren
                      <ArrowRight size={15} strokeWidth={1.5} />
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="stimme-teaser" className="px-6 py-24 md:px-12 md:py-36">
        <div className="mx-auto max-w-4xl text-center">
          <Reveal>
            <div aria-hidden="true" className="mx-auto mb-10 h-px w-24 bg-sand" />
            <blockquote className="font-serif text-2xl italic leading-relaxed text-rot md:text-4xl md:leading-snug">
              „Jede Yogastunde mit Carmen ist eine Wohltat.“
            </blockquote>
            <p className="mt-6 text-xs uppercase tracking-[0.35em] text-sand">Petra</p>
            <div aria-hidden="true" className="mx-auto mt-10 h-px w-24 bg-sand" />
          </Reveal>
          <Reveal delay={0.15} className="mt-12">
            <Link
              to="/kundenstimmen"
              data-testid="stimme-teaser-link"
              className="inline-flex items-center gap-3 rounded-full border border-rot px-8 py-3.5 text-sm tracking-[0.15em] text-rot transition-all duration-500 hover:bg-rot hover:text-creme"
            >
              Kundenstimmen
              <ArrowUpRight size={16} strokeWidth={1.5} />
            </Link>
          </Reveal>
        </div>
      </section>

      <section data-testid="kontakt-strip" className="px-6 pb-28 pt-4 md:px-12 md:pb-36">
        <div className="mx-auto flex max-w-6xl flex-col items-start justify-between gap-10 md:flex-row md:items-end">
          <Reveal>
            <h2 className="font-serif text-5xl leading-none text-rot md:text-7xl">Kontakt</h2>
          </Reveal>
          <Reveal delay={0.1}>
            <Link
              to="/kontakt"
              data-testid="kontakt-strip-link"
              className="group inline-flex items-center gap-4 text-sm uppercase tracking-[0.3em] text-rot"
            >
              <span className="border-b border-rot/40 pb-1 transition-colors duration-500 group-hover:border-rot">
                Zur Kontaktseite
              </span>
              <ArrowUpRight
                size={18}
                strokeWidth={1.5}
                className="transition-transform duration-500 group-hover:rotate-45"
              />
            </Link>
          </Reveal>
        </div>
      </section>
    </PageShell>
  );
}
